
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  SidebarProvider, 
  SidebarTrigger,
  useSidebar 
} from "@/components/ui/sidebar";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import ThemeToggle from "./ThemeToggle";
import NotificationDropdown from "./NotificationDropdown";
import { AppSidebar } from "./navigation/AppSidebar";
import { 
  Menu,
  User,
  CreditCard,
  Settings,
  LogOut,
  Crown,
  Sparkles
} from "lucide-react";
import { Link } from "react-router-dom";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();

  const handleLogout = () => {
    signOut();
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen w-full flex bg-background">
        <AppSidebar />
        
        <main className="flex-1 flex flex-col">
          {/* Top bar */}
          <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border/20 bg-background/80 backdrop-blur-md px-6 shadow-sm">
            <SidebarTrigger className="hover:bg-accent/50 rounded-lg transition-colors" />
            
            <div className="flex-1" />
            
            <div className="flex items-center gap-4">
              <NotificationDropdown />
              <ThemeToggle />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full hover:bg-accent/50 ring-2 ring-primary/20 hover:ring-primary/40 transition-all duration-300">
                    <Avatar className="h-8 w-8 shadow-md">
                      <AvatarImage src={user?.avatarUrl} />
                      <AvatarFallback className="bg-gradient-to-br from-purple-600 to-blue-600 text-white font-bold text-sm">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-background/95 backdrop-blur-md border border-border/20 shadow-xl" align="end">
                  <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-t-lg">
                    <Avatar className="h-10 w-10 ring-2 ring-primary/30">
                      <AvatarImage src={user?.avatarUrl} />
                      <AvatarFallback className="bg-gradient-to-br from-purple-600 to-blue-600 text-white font-bold">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col gap-1">
                      <p className="font-semibold text-foreground text-sm">{user?.name || 'User'}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                        {user?.email}
                      </p>
                      <Badge 
                        variant={user?.plan === 'free' ? 'secondary' : 'default'}
                        className={`text-xs w-fit ${user?.plan !== 'free' ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' : ''}`}
                      >
                        {user?.plan === 'team' && <Crown className="w-3 h-3 mr-1" />}
                        {user?.plan === 'pro' && <Sparkles className="w-3 h-3 mr-1" />}
                        {(user?.plan || 'free').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-2">
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer rounded-lg font-medium hover:bg-primary/10">
                        <User className="mr-3 h-4 w-4" />
                        Profile Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/billing" className="cursor-pointer rounded-lg font-medium hover:bg-primary/10">
                        <CreditCard className="mr-3 h-4 w-4" />
                        Billing & Plans
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="rounded-lg font-medium hover:bg-accent/50">
                      <Settings className="mr-3 h-4 w-4" />
                      Preferences
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-2 bg-border/20" />
                    <DropdownMenuItem 
                      onClick={handleLogout} 
                      className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-500/10 rounded-lg font-medium"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Page content */}
          <div className="flex-1 overflow-auto">
            <div className="p-6">
              {children}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default DashboardLayout;
